import React, { Fragment, useEffect, useState } from 'react';
import GlobalStyles from '../../styles/globalStyles';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  FeedPage,
  ForbiddenPage,
  HomePage,
  InfluencerProfilePage,
  PageNotFound,
  ReelsPage,
  SplashPage,
  TermsPage,
  WelcomePage,
} from '../../pages';
import { CLOSE_BUTTON_ROUTES, FEED, FORBIDDEN, HOME, INFLUENCER, REELS, TERMS, WELCOME } from './routes';
import { AuthLayout, PrivateLayout } from '../../layouts';
import { selectIsLoggedIn, setIsLoggedIn, setStartParams } from '../../store/slices/auth/slice';
import { Toaster } from 'sonner';
import { getUserInfo } from '../../store/slices/auth/asyncThunks';
import { selectOpenMediaPreview, setOpenMediaPreview } from '../../store/slices/influencer/slice';
import {
  DEFAULT_START_PARAMS,
  getTelegramStartParam,
  getTelegramWebApp,
  parseTelegramStartParam,
  triggerHaptic,
} from '../../utils/telegram';
import useVisualViewport from '../../utils/useVisualViewport';

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const openMediaPreview = useSelector(selectOpenMediaPreview);
  const [minimumDurationPassed, setMinimumDurationPassed] = useState(false);
  const { pathname } = useLocation();

  // Track the keyboard geometry and keep the page pinned so the soft keyboard
  // overlays the (fixed-height) content instead of pushing / resizing it.
  useVisualViewport();

  useEffect(() => {
    const TelegramWebApp = getTelegramWebApp();

    if (!TelegramWebApp) {
      return undefined;
    }

    const handleBack = () => {
      triggerHaptic();
      if (pathname.includes('creators')) {
        if (openMediaPreview) {
          dispatch(setOpenMediaPreview(false));
        } else {
          navigate(-1);
        }
      } else {
        navigate(-1);
      }
    };

    if (CLOSE_BUTTON_ROUTES.includes(pathname.replace('/', ' ').trim())) {
      TelegramWebApp.BackButton.hide();
    } else {
      TelegramWebApp.BackButton.show();
      TelegramWebApp.BackButton.onClick(handleBack);
    }

    return () => {
      TelegramWebApp.BackButton.offClick(handleBack);
    };
  }, [pathname, openMediaPreview, navigate, dispatch]);

  useEffect(() => {
    const TelegramWebApp = getTelegramWebApp();
    let lockedHeight = 0;

    // Lock the app to its largest height so the keyboard overlays a fixed box
    // instead of resizing / pushing the content. Note: on iOS Telegram the
    // WebView (and viewportStableHeight) actually shrinks when the keyboard
    // opens, so the "never shrink" guard below is what keeps the box stable.
    const lockHeight = () => {
      const height = TelegramWebApp?.viewportStableHeight || window.innerHeight;

      if (height > lockedHeight) {
        lockedHeight = height;
        document.documentElement.style.setProperty('--app-height', `${height}px`);
      }
    };

    // The fullscreen transition changes the stable height; re-measure once it
    // settles (viewportStableHeight is not updated synchronously).
    const handleFullscreen = () => {
      lockedHeight = 0;
      setTimeout(lockHeight, 500);
    };

    lockHeight();
    window.addEventListener('resize', lockHeight);
    TelegramWebApp?.onEvent('viewportChanged', lockHeight);
    TelegramWebApp?.onEvent('fullscreenChanged', handleFullscreen);

    return () => {
      window.removeEventListener('resize', lockHeight);
      TelegramWebApp?.offEvent('viewportChanged', lockHeight);
      TelegramWebApp?.offEvent('fullscreenChanged', handleFullscreen);
    };
  }, []);

  useEffect(() => {
    const TelegramWebApp = getTelegramWebApp();

    if (!TelegramWebApp) {
      return undefined;
    }

    // Expose how much room the device notch + Telegram's native top controls
    // (Close / ⌄ / ⋯) take, so overlays can avoid colliding with them — this
    // matters in fullscreen mode and when the keyboard shrinks the viewport.
    const applyInsets = () => {
      const safeTop = TelegramWebApp.safeAreaInset?.top || 0;
      const contentTop = TelegramWebApp.contentSafeAreaInset?.top || 0;
      document.documentElement.style.setProperty('--tg-top-inset', `${safeTop + contentTop}px`);
    };

    applyInsets();
    TelegramWebApp.onEvent('safeAreaChanged', applyInsets);
    TelegramWebApp.onEvent('contentSafeAreaChanged', applyInsets);
    TelegramWebApp.onEvent('fullscreenChanged', applyInsets);

    return () => {
      TelegramWebApp.offEvent('safeAreaChanged', applyInsets);
      TelegramWebApp.offEvent('contentSafeAreaChanged', applyInsets);
      TelegramWebApp.offEvent('fullscreenChanged', applyInsets);
    };
  }, []);

  useEffect(() => {
    const TelegramWebApp = getTelegramWebApp();
    const token = TelegramWebApp?.initData;

    if (token) {
      const { affiliateId, influencerId, reelId } = parseTelegramStartParam(getTelegramStartParam());

      dispatch(setStartParams({ affiliateId, influencerId, reelId }));
      dispatch(getUserInfo({ ...(affiliateId && !influencerId && { affiliateId }) }))
        .unwrap()
        .then(() => {
          if (reelId) {
            setTimeout(() => {
              navigate(`/${REELS}`, { replace: true, state: { sharedReelId: reelId } });
            }, 200);
          } else if (influencerId) {
            setTimeout(() => {
              navigate(`/${INFLUENCER.replace(':id', influencerId)}`);
            }, 200);
          }
        })
        .catch(() => {});
      TelegramWebApp.requestFullscreen();
      TelegramWebApp.disableVerticalSwipes();
      TelegramWebApp.enableClosingConfirmation();
      TelegramWebApp.lockOrientation();
    } else {
      dispatch(setStartParams(DEFAULT_START_PARAMS));
      dispatch(setIsLoggedIn(true));
    }
    // eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => setMinimumDurationPassed(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Fragment>
      <GlobalStyles />
      <Toaster
        position="bottom-right"
        closeButton={false}
        richColors
        toastOptions={{
          style: {
            borderRadius: 24,
            border: '1px solid #18181b',
            background: '#0a0b0c',
            color: 'white',
          },
        }}
      />
      {minimumDurationPassed && isLoggedIn !== null ? (
        <Routes>
          <Route element={<PrivateLayout />}>
            <Route path={HOME} element={<HomePage />}>
              <Route index element={<Navigate to={FEED} replace />} />
              <Route path={FEED} element={<FeedPage />} />
              <Route path={REELS} element={<ReelsPage />} />
            </Route>
            <Route path={INFLUENCER} element={<InfluencerProfilePage />} />
          </Route>
          <Route element={<AuthLayout />}>{<Route path={WELCOME} element={<WelcomePage />} />}</Route>
          <Route path={FORBIDDEN} element={<ForbiddenPage />} />
          <Route path={TERMS} element={<TermsPage />} />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      ) : (
        <SplashPage />
      )}
    </Fragment>
  );
};

export default App;
